import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import { Check, Sparkles, Pen, BookOpen, Zap, Upload, FileText } from 'lucide-react';

const STEPS = [
  { id: 'identity', title: 'Twin Identity', icon: Sparkles },
  { id: 'style', title: 'Style Setup', icon: Pen },
  { id: 'knowledge', title: 'Knowledge Setup', icon: BookOpen },
  { id: 'activate', title: 'Activate Skill', icon: Zap },
];

export default function OnboardingModal() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completingOnboarding, setCompletingOnboarding] = useState(false);

  // Step 1: Twin Identity
  const [twinName, setTwinName] = useState('');
  const [twinDescription, setTwinDescription] = useState('');
  const [tone, setTone] = useState('friendly');

  // Step 2: Style Setup
  const [styleText, setStyleText] = useState('');
  const [styleInputMethod, setStyleInputMethod] = useState('paste'); // 'paste' or 'skip'

  // Step 3: Knowledge Setup
  const [knowledgeText, setKnowledgeText] = useState('');
  const [knowledgeFile, setKnowledgeFile] = useState(null);
  const [knowledgeInputMethod, setKnowledgeInputMethod] = useState('paste'); // 'paste', 'upload', or 'skip'

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleComplete = async () => {
    setCompletingOnboarding(true);
    setLoading(true);
    
    try {
      // Step 1: Create/Update Profile
      await apiClient.post('/personal/profile', {
        twinName: twinName || 'My Twin',
        language: 'en',
        responseTone: tone,
        personality: {},
        trainingModes: [],
        introductionSamples: [twinDescription],
      });

      // Step 2: Submit Style Training Data (if provided)
      if (styleText.trim()) {
        await apiClient.post('/vault/upload', 
          createFormData({
            title: 'Onboarding Style Sample',
            description: 'Style training data from onboarding',
            type: 'training_text',
            tags: 'style,onboarding',
            allowForAI: 'true',
            intent: 'Style',
          }, new Blob([styleText], { type: 'text/plain' }), 'style-sample.txt')
        );
      }

      // Step 3: Submit Knowledge Training Data (if provided)
      if (knowledgeText.trim()) {
        await apiClient.post('/vault/upload', 
          createFormData({
            title: 'Onboarding Knowledge Sample',
            description: 'Knowledge training data from onboarding',
            type: 'training_text',
            tags: 'knowledge,onboarding',
            allowForAI: 'true',
            intent: 'Knowledge',
          }, new Blob([knowledgeText], { type: 'text/plain' }), 'knowledge-sample.txt')
        );
      }

      if (knowledgeFile) {
        await apiClient.post('/vault/upload', 
          createFormData({
            title: knowledgeFile.name,
            description: 'Knowledge document from onboarding',
            type: 'document',
            tags: 'knowledge,onboarding',
            allowForAI: 'true',
            intent: 'Knowledge',
          }, knowledgeFile, knowledgeFile.name)
        );
      }

      // Step 4: Complete Onboarding (auto-installs default skill)
      await apiClient.post('/personal/complete-onboarding');

      toast.success(t('success.saved'));
      
      // Redirect to My Twin page with onboarding parameter
      setTimeout(() => {
        navigate('/personal/twin?onboarding=complete');
      }, 1000);
    } catch (error) {
      console.error('Onboarding error:', error);
      const errorMessage = error.response?.data?.detail || t('errors.saveFailed');
      toast.error(errorMessage);
      setLoading(false);
      setCompletingOnboarding(false);
    }
  };

  const createFormData = (fields, file, fileName) => {
    const formData = new FormData();
    Object.keys(fields).forEach(key => {
      formData.append(key, fields[key]);
    });
    formData.append('file', file, fileName);
    return formData;
  };

  const canProceed = () => {
    if (currentStep === 0) return twinName.trim().length > 0;
    return true; // All other steps are optional
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-8 text-white">
          <h1 className="text-3xl font-bold mb-2">{t('onboarding.welcome')} ✨</h1>
          <p className="text-purple-100">{t('onboarding.subtitle')}</p>
        </div>

        {/* Progress Bar */}
        <div className="px-8 pt-6">
          <div className="flex justify-between items-center mb-2">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div key={step.id} className="flex flex-col items-center flex-1 relative">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold mb-2 transition-all ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isActive
                        ? 'bg-purple-600 text-white ring-4 ring-purple-200'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? <Check className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                  </div>
                  <p className="text-xs font-medium text-gray-600 text-center">{step.title}</p>
                  
                  {/* Connector Line */}
                  {index < STEPS.length - 1 && (
                    <div
                      className={`absolute top-6 left-1/2 w-full h-0.5 -z-10 transition-all ${
                        isCompleted ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                      style={{ marginLeft: '50%' }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* Step 1: Twin Identity */}
          {currentStep === 0 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('onboarding.step1Title')}</h2>
                <p className="text-gray-600">{t('onboarding.step1Desc')}</p>
              </div>

              <div>
                <Label htmlFor="twinName" className="text-base font-semibold">{t('onboarding.twinName')} *</Label>
                <Input
                  id="twinName"
                  value={twinName}
                  onChange={(e) => setTwinName(e.target.value)}
                  placeholder={t('onboarding.twinNamePlaceholder')}
                  className="mt-2 text-base h-12"
                  autoFocus
                />
              </div>

              <div>
                <Label htmlFor="twinDescription" className="text-base font-semibold">{t('onboarding.shortDescription')}</Label>
                <p className="text-sm text-gray-500 mt-1 mb-2">{t('onboarding.twinWillAct')}</p>
                <Textarea
                  id="twinDescription"
                  value={twinDescription}
                  onChange={(e) => setTwinDescription(e.target.value)}
                  placeholder={t('onboarding.twinDescPlaceholder')}
                  className="mt-2 min-h-[100px]"
                  rows={4}
                />
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">{t('onboarding.toneBaseline')}</Label>
                <RadioGroup value={tone} onValueChange={setTone} className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'casual', label: t('onboarding.casual'), desc: t('onboarding.casualDesc') },
                    { value: 'professional', label: t('onboarding.professional'), desc: t('onboarding.professionalDesc') },
                    { value: 'friendly', label: t('onboarding.friendly'), desc: t('onboarding.friendlyDesc') },
                    { value: 'direct', label: t('onboarding.direct'), desc: t('onboarding.directDesc') },
                  ].map((option) => (
                    <div
                      key={option.value}
                      className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        tone === option.value
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                      onClick={() => setTone(option.value)}
                    >
                      <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor={option.value} className="font-semibold cursor-pointer">
                          {option.label}
                        </Label>
                        <p className="text-sm text-gray-600 mt-0.5">{option.desc}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          {/* Step 2: Style Setup */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('onboarding.step2Title')}</h2>
                <p className="text-gray-600">{t('onboarding.step2Desc')}</p>
              </div>

              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Pen className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('onboarding.styleTraining')}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('onboarding.styleTrainingDesc')}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="styleText" className="text-base font-semibold">{t('onboarding.writingSample')}</Label>
                <Textarea
                  id="styleText"
                  value={styleText}
                  onChange={(e) => setStyleText(e.target.value)}
                  placeholder={t('onboarding.writingSamplePlaceholder')}
                  className="mt-2 min-h-[200px]"
                  rows={8}
                />
                <p className="text-sm text-gray-500 mt-2">{t('onboarding.writingTip')}</p>
              </div>

              <div className="flex items-center justify-center py-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600">{t('onboarding.dontHaveSample')}</p>
                  <Button variant="ghost" onClick={handleSkip} className="text-purple-600 hover:text-purple-700">
                    {t('onboarding.skipForNow')} →
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Knowledge Setup */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('onboarding.step3Title')}</h2>
                <p className="text-gray-600">{t('onboarding.step3Desc')}</p>
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('onboarding.knowledgeTraining')}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('onboarding.knowledgeTrainingDesc')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    variant={knowledgeInputMethod === 'paste' ? 'default' : 'outline'}
                    onClick={() => setKnowledgeInputMethod('paste')}
                    className="flex-1"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {t('onboarding.pasteText')}
                  </Button>
                  <Button
                    variant={knowledgeInputMethod === 'upload' ? 'default' : 'outline'}
                    onClick={() => setKnowledgeInputMethod('upload')}
                    className="flex-1"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {t('onboarding.uploadDocument')}
                  </Button>
                </div>

                {knowledgeInputMethod === 'paste' && (
                  <div>
                    <Label htmlFor="knowledgeText" className="text-base font-semibold">{t('onboarding.knowledgeContent')}</Label>
                    <Textarea
                      id="knowledgeText"
                      value={knowledgeText}
                      onChange={(e) => setKnowledgeText(e.target.value)}
                      placeholder={t('onboarding.knowledgeContentPlaceholder')}
                      className="mt-2 min-h-[180px]"
                      rows={7}
                    />
                  </div>
                )}

                {knowledgeInputMethod === 'upload' && (
                  <div>
                    <Label htmlFor="knowledgeFile" className="text-base font-semibold">{t('onboarding.uploadDocumentBtn')}</Label>
                    <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-all">
                      <input
                        id="knowledgeFile"
                        type="file"
                        accept=".txt,.pdf,.doc,.docx,.md"
                        onChange={(e) => setKnowledgeFile(e.target.files[0])}
                        className="hidden"
                      />
                      <label htmlFor="knowledgeFile" className="cursor-pointer">
                        <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        {knowledgeFile ? (
                          <p className="text-sm font-medium text-gray-900">{knowledgeFile.name}</p>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-gray-700">{t('onboarding.clickToUpload')}</p>
                            <p className="text-xs text-gray-500 mt-1">{t('onboarding.fileFormats')}</p>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center py-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600">{t('onboarding.dontHaveKnowledge')}</p>
                  <Button variant="ghost" onClick={handleSkip} className="text-purple-600 hover:text-purple-700">
                    {t('onboarding.skipForNow')} →
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Activate First Skill */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full mb-4">
                  <Zap className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('onboarding.step4Title')}</h2>
                <p className="text-gray-600">{t('onboarding.step4Desc')}</p>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('onboarding.emailAssistant')}</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {t('onboarding.emailAssistantDesc')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        ✓ {t('onboarding.draftEmails')}
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        ✓ {t('onboarding.replySuggestions')}
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        ✓ {t('onboarding.toneAdjustment')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">{t('onboarding.whatHappensNext')}</h4>
                <ul className="space-y-2 text-sm text-green-800">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{t('onboarding.autoInstall')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{t('onboarding.redirectAssistant')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{t('onboarding.exploreMarketplace')}</span>
                  </li>
                </ul>
              </div>

              <div className="text-center pt-4">
                <p className="text-sm text-gray-500">{t('onboarding.twinReady')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-8 py-6 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {t('onboarding.step')} {currentStep + 1} {t('onboarding.of')} {STEPS.length}
              </div>
              {currentStep > 0 && (
                <Button
                  onClick={handleBack}
                  disabled={loading || completingOnboarding}
                  variant="ghost"
                  size="sm"
                  className="text-gray-600 hover:text-gray-900"
                >
                  ← {t('common.back')}
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              {currentStep === STEPS.length - 1 ? (
                <Button
                  onClick={handleComplete}
                  disabled={loading}
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8"
                >
                  {completingOnboarding ? (
                    <>
                      <span className="animate-spin mr-2">⚡</span>
                      {t('onboarding.settingUp')}
                    </>
                  ) : (
                    <>{t('onboarding.completeSetup')} ✨</>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  size="lg"
                  className="px-8"
                >
                  {t('common.continue')} →
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
